// src/pages/auth/Register.tsx
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { PasswordInput } from '@/components/PasswordInput'
import { Turnstile } from '@/components/Turnstile'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import { toast } from 'sonner'
import Loading from '@/components/Loading'
import { useOwnerRegisterMutation } from '@/hooks/useAuth'
import { useActivatePlanMutation, type PlanKey } from '@/hooks/usePlans'
import { useStore } from '@/store/Store'
import { getOrCreateDeviceId } from '@/lib/deviceId'
import { useEffect, useRef, useState } from 'react'

const formSchema = z
  .object({
    name: z.string().min(2, 'Nom trop court'),
    email: z.string().email('Email invalide'),
    password: z.string().min(8, 'Min 8 caractères'),
    confirmPassword: z.string().min(8, 'Min 8 caractères'),
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: 'Les mots de passe ne correspondent pas',
    path: ['confirmPassword'],
  })

type FormValues = z.infer<typeof formSchema>

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string

export default function Register() {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: '', email: '', password: '', confirmPassword: '' },
  })

  const navigate = useNavigate()
  const { search } = useLocation()
  const redirect = new URLSearchParams(search).get('redirect') ?? '/dashboard'
  const [params] = useSearchParams()
  const plan = params.get('plan') as PlanKey | null

  const { state } = useStore()
  const { userInfo } = state

  const { mutateAsync: registerOwner, isPending } = useOwnerRegisterMutation()
  const { mutateAsync: activatePlan, isPending: isActivating } =
    useActivatePlanMutation()

  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [registered, setRegistered] = useState(false)

  // Un utilisateur déjà connecté qui arrive sur /register?plan=... (ex: lien
  // "S'abonner" de la page pricing) ne doit pas revoir le formulaire
  // d'inscription : on active juste le plan sur son compte existant.
  const didAutoRef = useRef(false)

  useEffect(() => {
    const auto = async () => {
      if (didAutoRef.current) return
      if (userInfo) {
        try {
          didAutoRef.current = true
          if (plan) {
            await activatePlan(plan)
            toast.success('Plan activé')
          }
        } catch (e: any) {
          toast.error(
            e?.response?.data?.message ?? 'Activation du plan impossible'
          )
        } finally {
          navigate(redirect, { replace: true })
        }
      }
    }
    auto()
  }, [userInfo, plan, activatePlan, navigate, redirect])

  async function onSubmit(values: FormValues) {
    try {
      await registerOwner({
        name: values.name,
        email: values.email.trim().toLowerCase(),
        password: values.password,
        deviceId: getOrCreateDeviceId(),
        ...(turnstileToken ? { turnstileToken } : {}),
      })

      if (plan) {
        localStorage.setItem('pendingPlanActivation', plan)
      }
      setRegistered(true)
    } catch (e: any) {
      if (e?.response?.data?.code === 'CHALLENGE_REQUIRED') {
        setTurnstileToken(null)
        toast.info('Merci de compléter la vérification ci-dessous.')
        return
      }
      toast.error(e?.response?.data?.message ?? 'Inscription impossible')
    }
  }

  if (userInfo && (isActivating || plan)) {
    return (
      <div className='grid place-items-center h-[100vh]'>
        <Loading />
      </div>
    )
  }

  if (registered) {
    return (
      <div className='flex flex-row items-center justify-center h-[100vh] bg-gradient-to-br from-[hsl(0,0%,100%)] via-[hsl(140,13%,91%)] to-[hsl(144,80%,73%)]'>
        <Card className='w-[520px] shadow-2xl rounded-xl'>
          <CardHeader>
            <p className='font-semibold text-xl'>Vérifiez votre email</p>
          </CardHeader>
          <CardContent>
            <p className='text-muted-foreground'>
              Un email de confirmation vient de vous être envoyé. Cliquez sur
              le lien qu'il contient pour activer votre compte.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className='flex flex-row items-center justify-center h-[100vh] bg-gradient-to-br from-[hsl(0,0%,100%)] via-[hsl(140,13%,91%)] to-[hsl(144,80%,73%)]'>
      <Card className='w-[520px] shadow-2xl rounded-xl'>
        <CardHeader>
          <div className='flex flex-row items-center gap-3'>
            {/* Mode clair */}
            <img
              src='/logo_mogesapp_light.png'
              alt='MOGESAPP'
              className='h-12 w-12 dark:hidden'
              loading='eager'
            />
            {/* Mode sombre */}
            <img
              src='/logo_mogesapp_dark.png'
              alt='MOGESAPP'
              className='h-12 w-12 hidden dark:block'
              loading='eager'
            />
            <h1 className='font-bold text-primary text-4xl'>MOGESAPP</h1>
          </div>
          <hr className='pt-5' />
          <p className='font-semibold text-xl'>Créer un compte</p>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
              <FormField
                control={form.control}
                name='name'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nom</FormLabel>
                    <FormControl>
                      <Input placeholder='Votre nom' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='email'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder='Votre email' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='password'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Mot de passe</FormLabel>
                    <FormControl>
                      <PasswordInput placeholder='Mot de passe' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='confirmPassword'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirmer le mot de passe</FormLabel>
                    <FormControl>
                      <PasswordInput placeholder='Confirmation' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Turnstile
                siteKey={TURNSTILE_SITE_KEY}
                onVerify={(token) => setTurnstileToken(token)}
              />
              {isPending ? (
                <Loading />
              ) : (
                <Button type='submit' disabled={!turnstileToken}>
                  Valider
                </Button>
              )}
            </form>
          </Form>
        </CardContent>
        <CardFooter className='text-muted-foreground flex justify-center text-center '>
          <div className='mt-5 font-extralight text-sm'>
            Déjà un compte ?{' '}
            <Link to='/login' className='text-primary hover:text-primary/50'>
              Connectez-vous
            </Link>
          </div>
        </CardFooter>
      </Card>
    </div>
  )
}
